import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { AGENTS } from "@/data/agents";
import { Corners, PageHead, Reveal, StatusDot, Tag } from "@/components/ui-kit";

const AgentsPage = () => {
  const [active, setActive] = useState(null);
  return (
    <div data-testid="agents-page" className="pb-24">
      <PageHead
        eyebrow="MODULE 02 // INTELLIGENCE"
        title="AI AGENTS"
        subtitle="Intelligence that coordinates the machine economy. Agents plan, discover machines, negotiate resources and authorize settlement."
        note="FICTIONAL INTERFACE EXAMPLES — NOT REAL DEPLOYED AGENTS"
      />
      <div className="px-5 sm:px-8 max-w-7xl mx-auto grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {AGENTS.map((a, i) => (
          <Reveal key={a.id} delay={i * 0.06}>
            <button
              data-testid={`agent-card-${a.id.toLowerCase()}`}
              data-cursor="SCAN"
              onClick={() => setActive(a)}
              className="hud-panel relative w-full text-left p-5 group hover:border-arc-electric/60 transition-colors duration-300"
              aria-haspopup="dialog"
            >
              <Corners className="border-arc-electric/0 group-hover:border-arc-electric/70 transition-colors duration-300" />
              <div className="flex items-center justify-between mb-4">
                <span className="font-mono text-base text-arc-white tracking-wider">{a.id}</span>
                <span className="flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] text-arc-silver/60"><StatusDot status={a.status} />{a.status}</span>
              </div>
              <div className="font-mono text-[10px] tracking-[0.3em] text-arc-electric mb-4">{a.type}</div>
              <div className="grid grid-cols-3 gap-2 font-mono text-[11px]">
                <div><div className="text-arc-silver/40 text-[9px] tracking-[0.2em] mb-0.5">TASKS</div><div className="text-arc-silver">{a.tasks}</div></div>
                <div><div className="text-arc-silver/40 text-[9px] tracking-[0.2em] mb-0.5">REP</div><div className="text-arc-green">{a.reputation}</div></div>
                <div><div className="text-arc-silver/40 text-[9px] tracking-[0.2em] mb-0.5">BALANCE</div><div className="text-arc-cyan">{a.balance}</div></div>
              </div>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {a.capabilities.slice(0, 3).map((c) => <Tag key={c}>{c}</Tag>)}
                {a.capabilities.length > 3 && <Tag tone="silver">+{a.capabilities.length - 3}</Tag>}
              </div>
              <div className="mt-4 font-mono text-[9px] tracking-[0.25em] text-arc-silver/30 group-hover:text-arc-cyan/70 transition-colors">&gt; OPEN PROFILE_</div>
            </button>
          </Reveal>
        ))}
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-arc-black/85 backdrop-blur-sm"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
            role="dialog" aria-modal="true" aria-label={`${active.id} profile`}
          >
            <motion.div
              data-testid="agent-modal"
              className="hud-panel relative w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6 sm:p-8 scanlines"
              initial={{ scale: 0.96, y: 16 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, y: 16 }}
              transition={{ duration: 0.25 }}
              onClick={(e) => e.stopPropagation()}
            >
              <Corners />
              <button onClick={() => setActive(null)} data-testid="agent-modal-close" aria-label="Close agent profile" className="absolute top-4 right-4 text-arc-silver/50 hover:text-arc-cyan">
                <X size={18} />
              </button>
              <div className="flex items-center gap-3 mb-1">
                <StatusDot status={active.status} />
                <h3 className="font-display text-2xl font-bold text-arc-white tracking-wide">{active.id}</h3>
              </div>
              <div className="font-mono text-[10px] tracking-[0.3em] text-arc-electric mb-6">{active.type} · DEMO PROFILE</div>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <div className="font-mono text-[10px] tracking-[0.3em] text-arc-silver/40 mb-2">IDENTITY</div>
                  <div className="space-y-2 font-mono text-xs">
                    <div className="flex justify-between"><span className="text-arc-silver/50">REPUTATION</span><span className="text-arc-green">{active.reputation}</span></div>
                    <div className="flex justify-between"><span className="text-arc-silver/50">TASKS</span><span className="text-arc-silver">{active.tasks}</span></div>
                    <div className="flex justify-between"><span className="text-arc-silver/50">WALLET</span><span className="text-arc-cyan">{active.balance}</span></div>
                    <div className="flex justify-between"><span className="text-arc-silver/50">NETWORK</span><span className="text-arc-silver">ARC</span></div>
                  </div>
                  <div className="font-mono text-[10px] tracking-[0.3em] text-arc-silver/40 mt-5 mb-2">CAPABILITIES</div>
                  <div className="flex flex-wrap gap-1.5">{active.capabilities.map((c) => <Tag key={c}>{c}</Tag>)}</div>
                </div>
                <div>
                  <div className="font-mono text-[10px] tracking-[0.3em] text-arc-silver/40 mb-2">TASK HISTORY</div>
                  <div className="space-y-1.5 font-mono text-[11px]">
                    {active.history.map((h) => (
                      <div key={h.id} className="flex items-center justify-between border border-arc-blue/10 px-2.5 py-1.5">
                        <span className="text-arc-silver/70">{h.id}</span>
                        <span className={h.result === "SETTLED" || h.result === "VERIFIED" ? "text-arc-green" : h.result === "OPEN" ? "text-arc-cyan" : "text-amber-300"}>{h.result}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="font-mono text-[10px] tracking-[0.3em] text-arc-silver/40 mt-6 mb-2">ACTIVITY LOG</div>
              <div className="bg-arc-black/70 border border-arc-blue/10 p-3 font-mono text-[11px] space-y-1 text-arc-silver/60">
                {active.activity.map((l) => <div key={l}>&gt; {l}</div>)}
              </div>
              <div className="mt-4 font-mono text-[9px] tracking-[0.2em] text-arc-silver/30">FICTIONAL DEMO AGENT — NO REAL ON-CHAIN ACTIVITY</div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AgentsPage;
